import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { Patient } from '../../../core/models/patient.model';
import { Prescriber } from '../../../core/models/prescriber.model';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { PagedOutboundFaxesResult, PatientService } from '../../../core/services/patient.service';
import { OutboundFax } from '../../../core/models/outbound-fax.model';
import { OutboundFaxStatus } from '../../../core/enums/outbound-fax-status.enum';
import { OutboundFaxesService } from 'app/core/services/outbound-faxes.service';
import { keyedStatusOptions as keyedProviderOfficeStatusOptions } from 'app/core/options/provider-office-status.opts';
import { TitleCasePipe, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PaginationHeaderComponent } from '../pagination-header/pagination-header.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { PrescriberNameComponent } from '../prescriber-name/prescriber-name.component';
import { PhoneNumberComponent } from '../phone-number/phone-number.component';
import { CopyToClipboardComponent } from '../copy-to-clipboard/copy-to-clipboard.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { OutboundFaxTemplateComponent } from '../outbound-fax-template/outbound-fax-template.component';
import { ConsultNoteReceivedComponent } from '../consult-note-received/consult-note-received.component';
import { InboundCallReceivedComponent } from '../inbound-call-received/inbound-call-received.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PhoneNumberPipe } from '../../pipes/phone-number.pipe';

const CheckInterval = 3000;
const ScheduledSoonMinutes = 15;

const isActive = (outboundFax: OutboundFax) => (
  outboundFax.status === OutboundFaxStatus.pendingScheduling ||
  outboundFax.status === OutboundFaxStatus.queued ||
  (outboundFax.status === OutboundFaxStatus.scheduled &&
    DateTime.fromJSDate(new Date(outboundFax.scheduledTime)) < DateTime.now().plus({ minutes: ScheduledSoonMinutes }))
);

const trackFaxItemsBy = (_index: number, item: OutboundFax) => `${item.id}-${item.status}`;

const visibleStatuses = [
  OutboundFaxStatus.pendingScheduling,
  OutboundFaxStatus.scheduled,
  OutboundFaxStatus.queued,
  OutboundFaxStatus.skipped,
  OutboundFaxStatus.sent,
  OutboundFaxStatus.failed,
  OutboundFaxStatus.canceled,
];

@Component({
    selector: 'app-patient-outbound-faxes',
    templateUrl: './patient-outbound-faxes.component.html',
    styleUrls: ['./patient-outbound-faxes.component.scss'],
    imports: [RouterLink,
              PaginationHeaderComponent,
              PaginationComponent,
              PrescriberNameComponent,
              PhoneNumberComponent,
              CopyToClipboardComponent,
              FaIconComponent,
              OutboundFaxTemplateComponent,
              ConsultNoteReceivedComponent,
              InboundCallReceivedComponent,
              MatProgressSpinner,
              TitleCasePipe,
              DatePipe,
              PhoneNumberPipe]
})
export class PatientOutboundFaxesComponent implements OnInit, OnDestroy {
  private patientService = inject(PatientService);
  private outboundFaxesService = inject(OutboundFaxesService);

  @Input() prescriber: Prescriber;
  @Input() patient: Patient;

  paging = LimitOffsetPaging.empty;
  loadingFaxes = false;
  outboundFaxes: OutboundFax[] = [];
  outboundFaxStatuses = OutboundFaxStatus;
  trackItemsBy = trackFaxItemsBy;
  nextCheckTimeout = null;
  keyedProviderOfficeStatusOptions = keyedProviderOfficeStatusOptions;

  ngOnInit() {
    this.loadPatientFaxes(1);
  }

  ngOnDestroy() {
    this.clearNextCheckTimeout();
  }

  refreshPatientFaxes() {
    this.clearNextCheckTimeout();
    this.loadPatientFaxes(this.paging.currentPage);
  }

  cancel($event, outboundFax) {
    $event.preventDefault();

    this.outboundFaxesService.cancel(outboundFax.id).subscribe(() => {
      this.loadPatientFaxes(this.paging.currentPage, undefined, false);
    });
  }

  onPageSizeChange(size: number) {
    this.loadPatientFaxes(this.paging.currentPage, size)
  }

  onPageChange(page: number) {
    this.loadPatientFaxes(page)
  }

  private clearNextCheckTimeout() {
    if (this.nextCheckTimeout) {
      clearTimeout(this.nextCheckTimeout);
    }
  }

  private loadPatientFaxes(page: number, pageSize: number = this.paging.pageSize, showSpinner = true): void {
    this.loadingFaxes = showSpinner;
    this.patientService
      .getOutboundFaxes(this.patient.id, this.prescriber?.npi, visibleStatuses, { page, pageSize })
      .subscribe((results: PagedOutboundFaxesResult) => {

        this.loadingFaxes = false;
        this.outboundFaxes = results.records;
        this.paging = results.meta.paging;

        const anyActive: boolean = this.outboundFaxes.some(isActive);

        if (anyActive) {
          this.nextCheckTimeout = setTimeout(
            () => this.loadPatientFaxes(this.paging.currentPage, this.paging.pageSize, false), CheckInterval
          );
        }
      });
  }
}
